import type { VertexUsage } from "@/features/llm-usage/api/llm-usage.api";

/**
 * A gap this small is ingestion lag and rounding, not a discrepancy worth
 * flagging. Cloud Monitoring lands points on its own delay, so the tail of any
 * window is always slightly short.
 */
const RECONCILED_TOLERANCE = 0.02;

/**
 * The two directions of a gap mean different things. Vertex ahead of us is
 * unattributed spend; us ahead of Vertex is Monitoring lag or a metric it never
 * received, not spend to chase.
 */
export type VertexReconciliationStatus = "unavailable" | "reconciled" | "gap" | "shortfall";

/**
 * The token classes Nexus recorded for the same window Vertex reported.
 *
 * Vertex's Cloud Monitoring series are input and output only. Thinking and
 * cache are billed, and they sit in our table, but they are not in those
 * series — comparing Vertex's total against our `totalTokens` reads as a
 * shortfall that is really just a different split.
 */
export interface RecordedLlmUsageForVertex {
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
}

export interface VertexReconciliationSummary {
  status: VertexReconciliationStatus;
  /** Everything Vertex reported for the window, across every model. */
  vertexTotal: number;
  /** Size of the disagreement, unsigned — `status` carries its direction. */
  gapTokens: number;
  /**
   * Thinking plus cache writes and reads. Present on our side, absent from
   * Vertex's input/output metrics, so it is shown rather than compared.
   */
  residualTokens: number;
}

/**
 * Whether what Nexus recorded accounts for what Vertex billed.
 *
 * Shared by the trigger and the dialog it opens, so the sentence on the button
 * cannot drift from the one inside. Comparison is Vertex total vs Nexus
 * input+output; residual classes are exposed, not folded into the gap.
 */
export function summarizeVertexReconciliation(
  data: VertexUsage | undefined,
  recorded: RecordedLlmUsageForVertex,
): VertexReconciliationSummary {
  const residualTokens =
    recorded.thinkingTokens + recorded.cacheWriteTokens + recorded.cacheReadTokens;
  if (!data?.available) {
    return { status: "unavailable", vertexTotal: 0, gapTokens: 0, residualTokens };
  }

  const vertexTotal = data.byModel.reduce((sum, row) => sum + row.totalTokens, 0);
  const recordedComparable = recorded.inputTokens + recorded.outputTokens;
  const difference = vertexTotal - recordedComparable;
  const gapTokens = Math.abs(difference);

  const isReconciled =
    vertexTotal === 0
      ? recordedComparable === 0
      : gapTokens / vertexTotal <= RECONCILED_TOLERANCE;

  if (isReconciled) return { status: "reconciled", vertexTotal, gapTokens: 0, residualTokens };

  return {
    status: difference > 0 ? "gap" : "shortfall",
    vertexTotal,
    gapTokens,
    residualTokens,
  };
}
